/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100040 部門マスタメンテ
 */
import {
  SCodeSelect,
  SFrame,
  SLabel,
  SNumberField,
  STextField,
  msgState,
  SNormalButton,
  SFileButton,
  SConfirmDialog,
} from "components";
import { B100040Mode, getSCode, SakujoyoteiKbn, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, useFocus } from "states";
import { useNavigate } from "react-router-dom";
import { B000120, B000130, B000130Props, B100910 } from "pages";
import { getClientMessage } from "messages";
import {
  B000120EigyoNm,
  B100040Bumon,
  B100040Create,
  B100040Update,
  catchRestError,
} from "clients/rest";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  modeKbn: SCode;
  bumonCd: string;
  bumonNm: string;
  bumonKana: string;
  bumonNmr: string;
  telNo: string;
  faxNo: string;
  eigyoCd: string;
  eigyoNm: string;
  sakujoyoteiKbn: SCode;
  version: string;
  dirtyFields: {
    bumonCd: string;
    eigyoCd: string;
  };
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  modeKbn: getSCode(B100040Mode, "0"),
  bumonCd: "",
  bumonNm: "",
  bumonKana: "",
  bumonNmr: "",
  telNo: "",
  faxNo: "",
  eigyoCd: "",
  eigyoNm: "",
  sakujoyoteiKbn: getSCode(SakujoyoteiKbn, "0"),
  version: "",
  dirtyFields: {
    bumonCd: "",
    eigyoCd: "",
  },
};

const focusId = "B100040";
// 画面ID
const gamenId = "100040";

// B100040 部門マスタメンテ
export const B100040 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // dirtyFields初期化
  const resetDirtyFields = () => {
    const fields = methods.getValues();
    methods.setValue("dirtyFields.bumonCd", fields.bumonCd ?? "");
    methods.setValue("dirtyFields.eigyoCd", fields.eigyoCd ?? "");
  };

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 再レンダリング用フラグ
  const [reRender, setReRender] = useState(false);
  const reRendering = () => {
    resetDirtyFields();
    setReRender(!reRender);
  };

  // 活性設定
  // 新規モードかどうか
  const isNew = () => methods.getValues().modeKbn?.value === "0";

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  }, []);

  // モード変更時
  const handleChangeModeKbn = () => {
    // 新規の場合
    if (!isNew()) {
      methods.reset();
      methods.setValue("modeKbn", getSCode(B100040Mode, "1"));
    }
    // 修正の場合
    else {
      methods.reset();
    }

    reRendering();
  };
  // 更新
  const handleClickUpdateButton = () => {
    // 新規の場合
    if (isNew()) {
      B100040Create(methods)
        .then(() => {
          methods.reset();
          // ↓これがあると完了メッセージが表示される
          methods.setValue("bumonCd", "");
          setMsg(getClientMessage("B100040_001"));
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
    // 修正の場合
    else {
      B100040Update(methods)
        .then(() => {
          methods.reset();
          // ↓これがあると完了メッセージが表示される
          methods.setValue("bumonCd", "");
          setMsg(getClientMessage("B100040_002"));
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
  };

  // 部門コード変更
  const handleChangeBumonCd = () => {
    const fields = methods.getValues();
    if (fields.bumonCd === fields.dirtyFields?.bumonCd) return;

    (async () => {
      reRendering();
      // 部門情報表示
      await B100040Bumon(methods, navigate, setMsg);
    })();
  };

  // 営業所コード変更
  const handleChangeEigyoCd = () => {
    const fields = methods.getValues();
    if (fields.eigyoCd === fields.dirtyFields?.eigyoCd) return;

    (async () => {
      reRendering();
      // 営業所名表示
      await B000120EigyoNm(methods, navigate, setMsg);
    })();
  };

  // 部門照会ダイアログの引数を設定
  const [b000130DialogProps, setB000130DialogProps] = useState<B000130Props>();
  const updateB000130DialogProps = () => {
    const fields = methods.getValues();

    setB000130DialogProps({
      eigyoCd: fields.eigyoCd,
      deljogaiKbn: "1",
    });
    reRendering();
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="部門マスタメンテ"
          gridTemplateColumns="8rem 6rem 5rem 2rem 7rem 4rem 2rem 5rem "
        >
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            gridColumn="8"
            {...register(11)}
            dialog={<B100910 gamenId={gamenId} />}
          />
          <SLabel required gridColumn="1">
            モード
          </SLabel>
          <SCodeSelect
            name="modeKbn"
            label="モード"
            required
            gridColumn="span 2"
            {...register(1)}
            options={B100040Mode}
            onChange={() => handleChangeModeKbn()}
          />
          <SLabel required gridColumn="1">
            部門コード
          </SLabel>
          {isNew() ? (
            <SNumberField
              name="bumonCd"
              label="部門コード"
              code
              size={6}
              {...register(2)}
            />
          ) : (
            <SNumberField
              name="bumonCd"
              label="部門コード"
              code
              size={6}
              onBlur={handleChangeBumonCd}
              onClick={updateB000130DialogProps}
              dialog={<B000130 {...b000130DialogProps} />}
              handleSubmit={(data: any) => {
                methods.setValue("bumonCd", data.bumonCd);
                methods.setValue("bumonNm", data.bumonNm);
                methods.setValue("bumonKana", data.bumonKana);
                methods.setValue("bumonNmr", data.bumonNmr);
                methods.setValue("telNo", data.telNo);
                methods.setValue("faxNo", data.faxNo);
                methods.setValue("eigyoCd", data.eigyoCd);
                methods.setValue(
                  "sakujoyoteiKbn",
                  getSCode(SakujoyoteiKbn, data.sakujoyoteiKbn)
                );
                methods.setValue("version", data.version);

                // 営業所名を取得
                B000120EigyoNm(methods, navigate, setMsg);
              }}
              {...register(2)}
            />
          )}

          <SLabel required gridColumn="1">
            部門名
          </SLabel>
          <STextField
            name="bumonNm"
            label="部門名"
            gridColumn="span 4"
            code
            size={20}
            {...register(3)}
          />
          <SLabel gridColumn="1">カナ名</SLabel>
          <STextField
            name="bumonKana"
            label="カナ名"
            gridColumn="span 4"
            code
            size={20}
            {...register(4)}
          />
          <SLabel gridColumn="1">略名</SLabel>
          <STextField
            name="bumonNmr"
            label="部門名"
            gridColumn="span 3"
            code
            size={20}
            {...register(5)}
          />
          <SLabel gridColumn="1">電話番号</SLabel>
          <STextField
            name="telNo"
            label="電話番号"
            gridColumn="span 2"
            code
            size={15}
            {...register(6)}
          />
          <SLabel gridColumn="1">FAX番号</SLabel>
          <STextField
            name="faxNo"
            label="FAX番号"
            gridColumn="span 2"
            code
            size={15}
            {...register(7)}
          />
          <SLabel required gridColumn="1">
            営業所コード
          </SLabel>
          <SNumberField
            name="eigyoCd"
            label="営業所コード"
            code
            onBlur={handleChangeEigyoCd}
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCd", data.eigyoCd);
              methods.setValue("eigyoNm", data.eigyoNm);
              reRendering();
            }}
            size={6}
            {...register(8)}
          />
          <STextField
            name="eigyoNm"
            label="営業所名"
            gridColumn="span 3"
            size={20}
            disabled
          />
          <SLabel gridColumn="1">削除予定区分</SLabel>
          <SCodeSelect
            name="sakujoyoteiKbn"
            label="削除予定区分"
            {...register(9)}
            options={SakujoyoteiKbn}
          />
          <SNormalButton
            label={labels.buttons.update}
            align="right"
            gridColumn="8"
            {...register(10)}
            dialog={
              <SConfirmDialog
                title="マスタ更新"
                msg="更新します。よろしいですか？"
              />
            }
            dialogAction="confirm"
            handleSubmit={handleClickUpdateButton}
          />
        </SFrame>
      </FormProvider>
    </>
  );
};
